import { useState, useEffect, useCallback } from "react";
import { UserPlus, Trash2, X, Users, Mail, Shield, Calendar, Crown, UserCheck, KeyRound } from "lucide-react";
import AppShell from "../components/layout/AppShell";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import ResetPasswordModal from "../components/team/ResetPasswordModal";
import CreateMemberModal from "../components/team/CreateMemberModal";

var SPECIALTY_LABELS = { consorcio: "Consórcio", seguros: "Seguros", ambos: "Ambos", plano_saude: "Plano de Saúde" };
var AVATAR_COLORS = ["bg-[#3B82F6]", "bg-[#F26A4B]", "bg-amber-500", "bg-purple-500", "bg-emerald-500", "bg-pink-500", "bg-cyan-500"];

function getInitials(name) {
  if (!name) return "U";
  return name.split(" ").map(function (n) { return n[0]; }).join("").slice(0, 2).toUpperCase();
}
function formatDate(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch (e) { return "-"; }
}
function formatValue(v) {
  if (v == null || v === 0) return "R$ 0";
  if (v >= 1000) return "R$ " + (v / 1000).toFixed(1) + "k";
  return "R$ " + v.toFixed(0);
}

function AssignLeaderModal({ broker, leaders, onClose, onSave }) {
  var leaderState = useState(broker.team_leader_id || "");
  var loadingState = useState(false);

  async function handleSave() {
    loadingState[1](true);
    try {
      await api.put("/users/" + broker.id + "/assign-leader", { team_leader_id: leaderState[0] || null });
      onSave();
    } catch (e) { console.error(e); }
    finally { loadingState[1](false); }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm" data-testid="assign-leader-modal">
        <div className="bg-white rounded-2xl shadow-2xl p-6">
          <h3 className="text-lg font-bold text-[#0A2F3D] mb-2">Atribuir Lider</h3>
          <p className="text-sm text-gray-500 mb-4">Selecione o lider de equipe para <strong>{broker.name}</strong></p>
          <select value={leaderState[0]} onChange={function (e) { leaderState[1](e.target.value); }}
            className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm bg-white mb-4 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30"
            data-testid="assign-leader-select">
            <option value="">Sem lider</option>
            {leaders.map(function (l) { return <option key={l.id} value={l.id}>{l.name}</option>; })}
          </select>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
            <button onClick={handleSave} disabled={loadingState[0]}
              className="flex-1 px-4 py-2.5 bg-[#3B82F6] text-white rounded-xl text-sm font-medium hover:bg-[#2563EB] disabled:opacity-50" data-testid="assign-leader-submit">
              {loadingState[0] ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function DeleteMemberModal({ member, onClose, onConfirm }) {
  var loadingState = useState(false);
  async function handleDelete() {
    loadingState[1](true);
    await onConfirm(member.id);
    loadingState[1](false);
  }
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm" data-testid="delete-member-modal">
        <div className="bg-white rounded-2xl shadow-2xl p-6">
          <h3 className="text-lg font-bold text-[#0A2F3D] mb-2">Remover Membro</h3>
          <p className="text-sm text-gray-500 mb-5">Tem certeza que deseja remover <strong>{member.name}</strong> da equipe?</p>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
            <button onClick={handleDelete} disabled={loadingState[0]}
              className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 disabled:opacity-50"
              data-testid="delete-member-confirm">
              {loadingState[0] ? "Removendo..." : "Remover"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default function TeamPage() {
  var auth = useAuth();
  var isAdmin = auth.user?.role === "admin";
  var isTeamLeader = auth.user?.role === "team_leader";

  var membersState = useState([]);
  var leadersState = useState([]);
  var loadingState = useState(true);
  var showCreateState = useState(false);
  var deletingState = useState(null);
  var assigningState = useState(null);
  var resetPwdState = useState(null);
  var statsState = useState({});

  var fetchData = useCallback(async function () {
    loadingState[1](true);
    try {
      var teamRes = await api.get("/dashboard/team");
      var teamData = teamRes.data.team || [];
      var teamLeaders = teamRes.data.leaders || [];
      var teamAssistants = teamRes.data.assistants || [];
      var teamMap = {};
      teamData.forEach(function (t) { teamMap[t.id] = t; });

      // Enrich brokers with performance data
      var enrichedBrokers = teamData.map(function (t) {
        return {
          id: t.id, name: t.name, role: "broker", specialty: t.specialty,
          clients: t.clients || 0, sales: t.sales || 0, commission: t.commission || 0,
          conversion: t.conversion || 0, meetings_done: t.meetings_done || 0, meetings_scheduled: t.meetings_scheduled || 0,
        };
      });

      var leaderItems = teamLeaders.map(function (l) {
        return { id: l.id, name: l.name, email: l.email, role: "team_leader", specialty: l.specialty };
      });
      var assistantItems = teamAssistants.map(function (a) {
        return { id: a.id, name: a.name, email: a.email, role: "assistant", permissions: a.permissions || {} };
      });

      var allMembers = leaderItems.concat(assistantItems).concat(enrichedBrokers);
      membersState[1](allMembers);
      leadersState[1](leaderItems);

      var totalClients = 0, totalSales = 0, totalComm = 0;
      enrichedBrokers.forEach(function (b) { totalClients += b.clients; totalSales += b.sales; totalComm += b.commission; });
      statsState[1]({ totalMembers: allMembers.length, totalClients: totalClients, totalSales: totalSales, totalCommission: totalComm });
    } catch (err) { console.error(err); }
    finally { loadingState[1](false); }
  }, []);

  useEffect(function () { fetchData(); }, [fetchData]);

  async function handleDelete(id) {
    try {
      await api.delete("/users/" + id);
      deletingState[1](null);
      fetchData();
    } catch (err) { console.error(err); }
  }

  function handleCreated() { showCreateState[1](false); fetchData(); }
  function handleAssigned() { assigningState[1](null); fetchData(); }

  if (!isAdmin && !isTeamLeader && user?.role !== "assistant") {
    return (
      <AppShell activePage="equipe" title="Equipe">
        <div className="text-center py-16 text-gray-500">Acesso restrito</div>
      </AppShell>
    );
  }

  // Separate leaders, assistants and brokers for display
  var leaderMembers = membersState[0].filter(function (m) { return m.role === "team_leader"; });
  var assistantMembers = membersState[0].filter(function (m) { return m.role === "assistant"; });
  var brokerMembers = membersState[0].filter(function (m) { return m.role === "broker"; });

  return (
    <AppShell activePage="equipe" title="Gestao de Equipe">
      <div data-testid="team-page">
        {showCreateState[0] && <CreateMemberModal onClose={function () { showCreateState[1](false); }} onSave={handleCreated} leaders={leadersState[0]} />}
        {deletingState[0] && <DeleteMemberModal member={deletingState[0]} onClose={function () { deletingState[1](null); }} onConfirm={handleDelete} />}
        {assigningState[0] && <AssignLeaderModal broker={assigningState[0]} leaders={leadersState[0]} onClose={function () { assigningState[1](null); }} onSave={handleAssigned} />}
        {resetPwdState[0] && <ResetPasswordModal member={resetPwdState[0]} onClose={function () { resetPwdState[1](null); }} />}

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6">
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#3B82F6]/10 rounded-xl"><Users className="w-5 h-5 text-[#3B82F6]" /></div>
              <span className="text-xs text-gray-500">{isTeamLeader ? "Minha Equipe" : "Membros"}</span>
            </div>
            <p className="text-xl lg:text-2xl font-bold text-[#0A2F3D]" data-testid="team-kpi-members">{statsState[0].totalMembers || 0}</p>
          </div>
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#F26A4B]/10 rounded-xl"><Shield className="w-5 h-5 text-[#F26A4B]" /></div>
              <span className="text-xs text-gray-500">Total Clientes</span>
            </div>
            <p className="text-xl lg:text-2xl font-bold text-[#0A2F3D]" data-testid="team-kpi-clients">{statsState[0].totalClients || 0}</p>
          </div>
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-emerald-500/10 rounded-xl"><Calendar className="w-5 h-5 text-emerald-500" /></div>
              <span className="text-xs text-gray-500">Total Vendas</span>
            </div>
            <p className="text-xl lg:text-2xl font-bold text-[#0A2F3D]" data-testid="team-kpi-sales">{statsState[0].totalSales || 0}</p>
          </div>
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-amber-500/10 rounded-xl"><Mail className="w-5 h-5 text-amber-500" /></div>
              <span className="text-xs text-gray-500">Comissoes</span>
            </div>
            <p className="text-xl lg:text-2xl font-bold text-emerald-600" data-testid="team-kpi-commission">{formatValue(statsState[0].totalCommission)}</p>
          </div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">{membersState[0].length} membro{membersState[0].length !== 1 ? "s" : ""} na equipe</p>
          {isAdmin && (
            <button onClick={function () { showCreateState[1](true); }}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#3B82F6] text-white rounded-xl text-sm font-medium hover:bg-[#2563EB] transition-colors"
              data-testid="team-add-member-button">
              <UserPlus className="w-4 h-4" /> Novo Membro
            </button>
          )}
        </div>

        {/* Team Leaders Section */}
        {isAdmin && leaderMembers.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-[#0A2F3D] mb-3 flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-500" /> Lideres de Equipe
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="leaders-grid">
              {leaderMembers.map(function (leader, idx) {
                var initials = getInitials(leader.name);
                var teamCount = brokerMembers.filter(function (b) { return b.team_leader_id === leader.id; }).length;
                return (
                  <div key={leader.id} className="bg-gradient-to-br from-amber-50 to-white rounded-xl lg:rounded-2xl p-5 shadow-sm border border-amber-200" data-testid={"leader-card-" + leader.id}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center ring-2 ring-amber-300">
                          <span className="text-white font-semibold">{initials}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-[#0A2F3D]">{leader.name}</p>
                          <p className="text-xs text-amber-600 font-medium">Lider de Equipe</p>
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-1">
                          <button onClick={function () { resetPwdState[1](leader); }}
                            className="p-1.5 hover:bg-amber-50 rounded-lg text-gray-300 hover:text-amber-500"
                            title="Resetar senha" data-testid={"leader-reset-pwd-" + leader.id}>
                            <KeyRound className="w-4 h-4" />
                          </button>
                          <button onClick={function () { deletingState[1](leader); }}
                            className="p-1.5 hover:bg-red-50 rounded-lg text-gray-300 hover:text-red-500"
                            data-testid={"leader-delete-" + leader.id}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                        {teamCount} corretor{teamCount !== 1 ? "es" : ""}
                      </span>
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-[#3B82F6]/10 text-[#3B82F6]">
                        {SPECIALTY_LABELS[leader.specialty] || leader.specialty || "Geral"}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">{leader.email}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Assistants Section */}
        {isAdmin && assistantMembers.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-[#0A2F3D] mb-3 flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-purple-500" /> Assistentes
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="assistants-grid">
              {assistantMembers.map(function (ast) {
                var initials = getInitials(ast.name);
                var perms = ast.permissions || {};
                var activeModules = Object.keys(perms).filter(function(k) { var p = perms[k]; return p && p.view; });
                return (
                  <div key={ast.id} className="bg-gradient-to-br from-purple-50 to-white rounded-xl lg:rounded-2xl p-5 shadow-sm border border-purple-200" data-testid={"assistant-card-" + ast.id}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center ring-2 ring-purple-300">
                          <span className="text-white font-semibold">{initials}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-[#0A2F3D]">{ast.name}</p>
                          <p className="text-xs text-purple-600 font-medium">Assistente</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={function () { resetPwdState[1](ast); }}
                          className="p-1.5 hover:bg-purple-50 rounded-lg text-gray-300 hover:text-purple-500"
                          title="Resetar senha" data-testid={"assistant-reset-pwd-" + ast.id}>
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button onClick={function () { deletingState[1](ast); }}
                          className="p-1.5 hover:bg-red-50 rounded-lg text-gray-300 hover:text-red-500"
                          data-testid={"assistant-delete-" + ast.id}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">{ast.email}</p>
                    <div className="flex flex-wrap gap-1">
                      {activeModules.map(function(mod) {
                        return <span key={mod} className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-purple-100 text-purple-700">{mod}</span>;
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Brokers Grid */}
        {loadingState[0] ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : brokerMembers.length === 0 ? (
          <div className="bg-white rounded-xl lg:rounded-2xl shadow-sm text-center py-16">
            <Users className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Nenhum corretor na equipe</p>
          </div>
        ) : (
          <>
            <h3 className="text-sm font-semibold text-[#0A2F3D] mb-3">Corretores</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="brokers-grid">
              {brokerMembers.map(function (broker, idx) {
                var initials = getInitials(broker.name);
                var avatarCls = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                var leaderName = null;
                if (broker.team_leader_id) {
                  var leader = leadersState[0].find(function (l) { return l.id === broker.team_leader_id; });
                  if (leader) leaderName = leader.name;
                }
                return (
                  <div key={broker.id} className="bg-white rounded-xl lg:rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={"broker-card-" + broker.id}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={"w-12 h-12 rounded-full flex items-center justify-center " + avatarCls}>
                          <span className="text-white font-semibold">{initials}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-[#0A2F3D]">{broker.name}</p>
                          <p className="text-xs text-gray-400">{broker.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {isAdmin && (
                          <button onClick={function () { assigningState[1](broker); }}
                            className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-300 hover:text-blue-500 transition-colors"
                            title="Atribuir lider" data-testid={"broker-assign-" + broker.id}>
                            <UserCheck className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={function () { resetPwdState[1](broker); }}
                            className="p-1.5 hover:bg-amber-50 rounded-lg text-gray-300 hover:text-amber-500 transition-colors"
                            title="Resetar senha" data-testid={"broker-reset-pwd-" + broker.id}>
                            <KeyRound className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={function () { deletingState[1](broker); }}
                            className="p-1.5 hover:bg-red-50 rounded-lg text-gray-300 hover:text-red-500 transition-colors"
                            data-testid={"broker-delete-" + broker.id}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-[#3B82F6]/10 text-[#3B82F6]">
                        {SPECIALTY_LABELS[broker.specialty] || broker.specialty || "Geral"}
                      </span>
                      {leaderName && (
                        <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                          <Crown className="w-3 h-3" /> {leaderName}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">desde {formatDate(broker.created_at)}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100">
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#0A2F3D]">{broker.clients}</p>
                        <p className="text-xs text-gray-400">Clientes</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#0A2F3D]">{broker.sales}</p>
                        <p className="text-xs text-gray-400">Vendas</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-emerald-600">{formatValue(broker.commission)}</p>
                        <p className="text-xs text-gray-400">Comissao</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Calendar className="w-3 h-3" />
                        <span>{broker.meetings_done} realizadas</span>
                        <span className="text-gray-200">|</span>
                        <span>{broker.meetings_scheduled} agendadas</span>
                      </div>
                      <span className="text-xs font-medium text-[#0A2F3D]">{broker.conversion}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
